function getLegalLang(): 'en' | 'es' {
  const stored = localStorage.getItem('wb_lang');
  return stored === 'es' ? 'es' : 'en';
}

function TermsEN() {
  return <>
    <h1>Terms of Use</h1>
    <p className="lp-legal-date">Last updated: July 2026</p>

    <h2>1. Description of the service</h2>
    <p>White Bot automates actions inside the video game <em>Whiteout Survival</em>. It is offered in two modalities:</p>
    <ul>
      <li><strong>White Bot Desktop (downloadable):</strong> client software that the user installs on their own PC (Windows + BlueStacks) under subscription. The bot runs on the user's machine and is controlled through White Bot's web panel.</li>
      <li><strong>White Bot Cloud (hosted):</strong> the bot runs on infrastructure operated by the provider. This modality is <strong>in development and available only as a private beta</strong>; it is not open for general purchase.</li>
    </ul>
    <p>Both modalities are <strong>intended for players with already established accounts</strong>. White Bot does not manage account creation processes, start-up tutorials, or onboarding of new game accounts.</p>

    <h2>2. Acceptance of terms</h2>
    <p>Accessing and using White Bot implies full and unreserved acceptance of these Terms of Use. If you disagree with any part, you must refrain from using the service.</p>

    <h2>3. Inherent risk of using bots — mandatory clause</h2>
    <p><strong>The user expressly declares knowledge and acceptance of the following:</strong></p>
    <ul>
      <li>The use of third-party automation software may violate the <strong>Terms of Service of Whiteout Survival</strong> published by Century Games and may lead to penalties by the game developer, including temporary restrictions, suspension, or permanent deletion of the game account.</li>
      <li>White Bot has no relationship or affiliation with Century Games or Whiteout Survival.</li>
      <li>White Bot cannot guarantee the absence of penalties. As of the publication date of these terms, no user has reported a confirmed ban attributable to White Bot, but this track record does not constitute a future guarantee.</li>
      <li>White Bot is programmed to mimic human behaviour (variable timings, natural pauses, realistic cycles), but no mechanism can guarantee 100% non-detection by the game developer.</li>
      <li>The user assumes full responsibility for their game account when using this service. White Bot shall not be liable for losses, penalties, bans, or any harm arising from such penalties.</li>
    </ul>

    <h2>4. Refund and ban policy</h2>
    <p>White Bot provides the technical automation service. The subscription pays for access to infrastructure and software, not for specific in-game results.</p>
    <ul>
      <li><strong>Ban due to inherent risk:</strong> if the user's game account is penalised by the developer as a consequence of the risk accepted in clause 3, no automatic subscription refund will be generated, as the technical service has functioned correctly.</li>
      <li><strong>Ban due to White Bot technical error:</strong> if the ban is directly attributable to a demonstrable technical fault of our platform, we will study a proportional refund for the unused service time, at the support team's discretion.</li>
      <li><strong>Voluntary cancellation:</strong> users may cancel at any time. Access remains active until the end of the current billing period and the remaining time is not prorated, except where required by applicable law.</li>
      <li><strong>Free trial:</strong> the free trial period does not admit monetary refund as it is free of charge. If a payment method is requested when starting the trial, no charge is made until the trial ends; cancelling before that incurs no cost.</li>
    </ul>

    <h2>5. Conditions of use</h2>
    <p>The user agrees to:</p>
    <ul>
      <li>Not share White Bot panel login credentials with third parties.</li>
      <li>Not attempt to access other users' slots or data.</li>
      <li>Not abuse the trial period by creating multiple accounts to obtain additional free days.</li>
      <li>Not use the service for illegal activities or activities that violate third-party rights.</li>
      <li>Notify the team of any security vulnerability discovered, rather than exploiting it.</li>
    </ul>

    <h2>6. Downloadable client software licence</h2>
    <p>The following applies to the <strong>White Bot Desktop</strong> modality:</p>
    <ul>
      <li>While the subscription is active, the user receives a <strong>limited, non-exclusive, non-transferable licence</strong> to install and use the White Bot client on a machine they own, for personal use.</li>
      <li>The client requires <strong>periodic connection to our servers to validate the licence</strong>. Without an active subscription (non-payment, cancellation, trial expiry), the client automatically stops operating.</li>
      <li>We may require minimum client versions: outdated versions may stop working until updated.</li>
      <li><strong>Minimum requirements:</strong> a Windows PC and a compatible BlueStacks version (published on the website). The user is responsible for their machine, network, and BlueStacks installation.</li>
      <li>Decompiling, modifying, redistributing, or circumventing the software's protections is prohibited.</li>
      <li>The game-sanction risk (clause 3) applies equally to this modality: the bot operates with the user's account on the user's own machine.</li>
    </ul>

    <h2>7. Subscription and billing</h2>
    <ul>
      <li>The subscription automatically renews at the start of each period (monthly or annual) unless cancelled in advance.</li>
      <li>Prices shown on the website include applicable taxes.</li>
      <li>The payment processor is Stripe. Card data is managed entirely by Stripe and never passes through White Bot's servers.</li>
      <li>We may modify prices with a minimum of 30 days' advance notice by email.</li>
    </ul>

    <h2>8. Service availability</h2>
    <p>We aim to offer reasonable service availability, but we do not guarantee a specific SLA during the beta phase. Interruptions may occur due to maintenance, updates, or unforeseen technical causes. Brief interruptions will not give rise to compensation, unless they are systematic and significant.</p>

    <h2>9. Intellectual property</h2>
    <p>White Bot's software, design, texts, and interfaces are the provider's property and protected by intellectual property law. The user acquires no rights over them beyond access to the service during the subscription period and the licence described in clause 6.</p>

    <h2>10. Limitation of liability</h2>
    <p>To the maximum extent permitted by applicable law, White Bot shall not be liable for:</p>
    <ul>
      <li>Loss of in-game progress, resources, or accounts.</li>
      <li>Penalties imposed by the game developer.</li>
      <li>Indirect damages, loss of profits, or harm arising from use or inability to use the service.</li>
      <li>Service interruptions beyond our infrastructure's control (game outages, emulator API changes, etc.).</li>
    </ul>
    <p>White Bot's total maximum liability to a user shall not exceed the amount paid by the user in the last 3 months of subscription.</p>

    <h2>11. Amendments</h2>
    <p>We may update these Terms of Use. If changes are material, registered users will be notified at least 15 days in advance by email. Continued use of the service after the new terms take effect implies acceptance.</p>

    <h2>12. Termination</h2>
    <p>White Bot reserves the right to suspend or cancel accounts that breach these Terms, with or without prior notice depending on the severity of the breach. No refund of the current subscription will be issued upon termination for breach.</p>

    <h2>13. Governing law and dispute resolution</h2>
    <p>These terms are governed by Spanish law. For any dispute, the parties submit to the competent courts under Spanish law, without prejudice to the consumer forum that may apply to the user.</p>
    <p>For queries or complaints: <a href="mailto:whitebot.admin@gmail.com">whitebot.admin@gmail.com</a></p>
  </>;
}

function TermsES() {
  return <>
    <h1>Términos de Uso</h1>
    <p className="lp-legal-date">Última actualización: julio de 2026</p>

    <h2>1. Descripción del servicio</h2>
    <p>White Bot automatiza acciones dentro del videojuego <em>Whiteout Survival</em>. Se ofrece en dos modalidades:</p>
    <ul>
      <li><strong>White Bot Descargable:</strong> software cliente que el usuario instala en su propio PC (Windows + BlueStacks) bajo suscripción. El bot se ejecuta en el equipo del usuario y se controla a través del panel web de White Bot.</li>
      <li><strong>White Bot Cloud (hosted):</strong> el bot se ejecuta en infraestructura operada por el proveedor. Esta modalidad está <strong>en desarrollo y disponible únicamente como beta privada</strong>; no está abierta a contratación general.</li>
    </ul>
    <p>Ambas modalidades están <strong>destinadas a jugadores con cuentas ya establecidas</strong>. White Bot no gestiona procesos de creación de cuenta, tutoriales de inicio ni incorporación de cuentas nuevas al juego.</p>

    <h2>2. Aceptación de los términos</h2>
    <p>El acceso y uso de White Bot implica la aceptación íntegra y sin reservas de estos Términos de Uso. Si no estás de acuerdo con alguna parte, debes abstenerte de usar el servicio.</p>

    <h2>3. Riesgo inherente al uso de bots — cláusula obligatoria</h2>
    <p><strong>El usuario declara conocer y aceptar expresamente lo siguiente:</strong></p>
    <ul>
      <li>El uso de software de automatización de terceros puede infringir los <strong>Términos de Servicio de Whiteout Survival</strong> publicados por Century Games y puede conllevar sanciones por parte del desarrollador del juego, incluyendo restricciones temporales, suspensión o eliminación permanente de la cuenta del juego.</li>
      <li>White Bot no tiene ninguna relación ni afiliación con Century Games ni con Whiteout Survival.</li>
      <li>White Bot no puede garantizar la ausencia de sanciones. A fecha de publicación de estos términos, ningún usuario ha reportado un baneo confirmado atribuible al uso de White Bot, pero este historial no constituye garantía futura.</li>
      <li>White Bot está programado para imitar comportamiento humano (tiempos variables, pausas naturales, ciclos realistas), pero ningún mecanismo puede garantizar al 100% la no detección por parte del desarrollador del juego.</li>
      <li>El usuario asume íntegramente el riesgo sobre su cuenta del juego al usar este servicio. White Bot no será responsable de pérdidas, sanciones, baneos ni cualquier perjuicio derivado de dichas sanciones.</li>
    </ul>

    <h2>4. Política de reembolsos y baneo</h2>
    <p>White Bot ofrece el servicio técnico de automatización. La suscripción retribuye el acceso a la infraestructura y al software, no resultados concretos dentro del juego.</p>
    <ul>
      <li><strong>Baneo por riesgo inherente:</strong> no se generará reembolso automático de la suscripción, ya que el servicio técnico ha funcionado correctamente.</li>
      <li><strong>Baneo por error técnico de White Bot:</strong> si el baneo es directamente atribuible a un fallo técnico demostrable de nuestra plataforma, estudiaremos el reembolso proporcional al tiempo de servicio no disfrutado.</li>
      <li><strong>Cancelación voluntaria:</strong> el usuario puede cancelar en cualquier momento. El acceso permanece activo hasta el final del período de facturación en curso y no se prorratea el tiempo restante.</li>
      <li><strong>Prueba gratuita:</strong> el período de prueba gratuito no admite reembolso económico al ser gratuito. Si al iniciar la prueba se solicita un método de pago, no se realiza ningún cargo hasta que la prueba finaliza; cancelando antes no hay coste alguno.</li>
    </ul>

    <h2>5. Condiciones de uso del servicio</h2>
    <ul>
      <li>No compartir credenciales de acceso al panel de White Bot con terceros.</li>
      <li>No intentar acceder a slots ni datos de otros usuarios.</li>
      <li>No abusar del período de trial creando múltiples cuentas para obtener días gratuitos adicionales.</li>
      <li>No utilizar el servicio para actividades ilegales o que vulneren derechos de terceros.</li>
      <li>Notificar al equipo cualquier vulnerabilidad de seguridad que detecte, en lugar de explotarla.</li>
    </ul>

    <h2>6. Licencia del software descargable</h2>
    <p>Lo siguiente aplica a la modalidad <strong>White Bot Descargable</strong>:</p>
    <ul>
      <li>Con la suscripción activa, el usuario recibe una <strong>licencia limitada, no exclusiva e intransferible</strong> para instalar y usar el cliente White Bot en un equipo de su propiedad, con fines personales.</li>
      <li>El cliente requiere <strong>conexión periódica con nuestros servidores para validar la licencia</strong>. Sin suscripción activa (impago, cancelación, expiración de la prueba), el cliente deja de operar automáticamente.</li>
      <li>Podemos exigir versiones mínimas del cliente: las versiones obsoletas pueden dejar de funcionar hasta actualizarse.</li>
      <li><strong>Requisitos mínimos:</strong> PC con Windows y una versión compatible de BlueStacks (publicados en la web). El usuario es responsable de su equipo, su red y su instalación de BlueStacks.</li>
      <li>Queda prohibido descompilar, modificar, redistribuir o eludir las protecciones del software.</li>
      <li>El riesgo de sanciones del juego (cláusula 3) aplica igualmente a esta modalidad: el bot opera con la cuenta del usuario en su propio equipo.</li>
    </ul>

    <h2>7. Suscripción y facturación</h2>
    <ul>
      <li>La suscripción se renueva automáticamente al inicio de cada período (mensual o anual) salvo cancelación previa.</li>
      <li>Los precios indicados en la web incluyen impuestos aplicables según la legislación española.</li>
      <li>El procesador de pagos es Stripe. Los datos de tarjeta son gestionados íntegramente por Stripe y nunca pasan por los servidores de White Bot.</li>
      <li>Podemos modificar los precios con un preaviso mínimo de 30 días por correo electrónico.</li>
    </ul>

    <h2>8. Disponibilidad del servicio</h2>
    <p>Nos comprometemos a ofrecer una disponibilidad razonable del servicio, pero no garantizamos un SLA específico durante la fase beta. Las interrupciones no prolongadas no darán derecho a compensación, salvo que sean sistemáticas y significativas.</p>

    <h2>9. Propiedad intelectual</h2>
    <p>El software, diseño, textos e interfaces de White Bot son propiedad del proveedor y están protegidos por la legislación de propiedad intelectual. El usuario no adquiere ningún derecho sobre ellos más allá del acceso al servicio durante la vigencia de su suscripción y la licencia descrita en la cláusula 6.</p>

    <h2>10. Limitación de responsabilidad</h2>
    <p>En la máxima medida permitida por la ley aplicable, White Bot no será responsable de:</p>
    <ul>
      <li>Pérdidas de progreso, recursos o cuenta dentro del juego.</li>
      <li>Sanciones impuestas por el desarrollador del juego.</li>
      <li>Daños indirectos, lucro cesante o perjuicios derivados del uso o la imposibilidad de uso del servicio.</li>
      <li>Interrupciones del servicio ajenas a nuestra infraestructura.</li>
    </ul>
    <p>La responsabilidad total máxima de White Bot frente a un usuario no superará el importe pagado por el usuario en los últimos 3 meses de suscripción.</p>

    <h2>11. Modificaciones de los términos</h2>
    <p>Podemos actualizar estos Términos de Uso. Si los cambios son materiales, notificaremos a los usuarios registrados con al menos 15 días de antelación por correo electrónico. El uso continuado del servicio tras la entrada en vigor de los nuevos términos implica su aceptación.</p>

    <h2>12. Terminación</h2>
    <p>White Bot se reserva el derecho de suspender o cancelar cuentas que incumplan estos Términos, con o sin previo aviso según la gravedad del incumplimiento.</p>

    <h2>13. Legislación aplicable y resolución de conflictos</h2>
    <p>Estos términos se rigen por la legislación española. Para cualquier consulta o reclamación: <a href="mailto:whitebot.admin@gmail.com">whitebot.admin@gmail.com</a></p>
  </>;
}

export function TermsPage() {
  const lang = getLegalLang();
  const privacyHref = lang === 'es' ? '/privacidad' : '/privacy';

  return (
    <div className="lp lp-legal-page">
      <nav className="lp-nav">
        <a href="/" className="lp-nav-logo"><img src="/logo.png" alt="White Bot" /></a>
        <div className="lp-nav-links">
          <a href="/">{ lang === 'es' ? 'Inicio' : 'Home' }</a>
          <a href="/dashboard" className="lp-btn-outline">{ lang === 'es' ? 'Entrar' : 'Log in' }</a>
        </div>
      </nav>
      <div className="lp-legal-shell">
        <div className="lp-legal-doc">
          {lang === 'es' ? <TermsES /> : <TermsEN />}
        </div>
      </div>
      <footer className="lp-footer">
        <div className="lp-container lp-footer-inner">
          <span className="lp-footer-logo">White Bot</span>
          <nav className="lp-footer-links">
            <a href="/">{ lang === 'es' ? 'Inicio' : 'Home' }</a>
            <a href={privacyHref}>{ lang === 'es' ? 'Política de privacidad' : 'Privacy policy' }</a>
            <a href="mailto:whitebot.admin@gmail.com">{ lang === 'es' ? 'Contacto' : 'Contact' }</a>
          </nav>
          <p className="lp-footer-copy">© 2025 White Bot · Beta</p>
        </div>
      </footer>
    </div>
  );
}
